import axios from 'axios';
import { refreshAccessToken as refreshAccessTokenAPI } from './apis/login.api';

// Create an empty Axios instance
const axiosInstance = axios.create({});

// Add a request interceptor to include the access token in headers
axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  }, async (error) => {
    const originalRequest = error?.config;
    // Handle 401 errors (unauthorized) which typically mean the access token has expired
    if (error?.response?.status === 401 && !originalRequest?._retry && !originalRequest?.url?.includes('/refresh')) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        // No refresh token available, must redirect to login
        clearStorageAndRedirect();
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh the access token using the refresh token
        const newTokens = await refreshAccessTokenAPI(refreshToken);
        // Save the new tokens
        localStorage.setItem('access_token', newTokens?.access_token);
        localStorage.setItem('refresh_token', newTokens?.refresh_token);

        // Update authorization headers for both axios defaults and the current request
        axios.defaults.headers.common['Authorization'] = `Bearer ${newTokens.access_token}`;
        originalRequest.headers['Authorization'] = `Bearer ${newTokens.access_token}`;

        // Retry the original request with the new token
        return axiosInstance(originalRequest);
      } catch (err) {
        console.error('Error refreshing access token:', err);

        // Only redirect to login if refresh token is invalid/expired (401/403)
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          console.log('Refresh token expired, redirecting to login');
          clearStorageAndRedirect();
          return Promise.reject(err);
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

function clearStorageAndRedirect() {
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = '/';
}

export default axiosInstance;